// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Save, User, Phone, Calendar, Church, Award, Users, LogOut, Camera, Upload, CheckCircle, Clock, Trash2, AlertTriangle } from 'lucide-react';
import crewAPI, { type Crew } from '../services/crewAPI-new';
import { useAuthStore } from '../store/authStore';
import { usersAPI } from '../services/api';
import Avatar from './common/Avatar';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  phone?: string;
  avatar?: string;
  birthday?: string;
  church?: string;
  position?: string;
  crewMemberships?: Array<{
    crew: {
      id: string;
      name: string;
      color: string;
    };
  }>;
}

interface UserSettingsPageProps {
  onClose: () => void;
  userCrew?: Crew | null;
  onCrewUpdate?: (crew: Crew | null) => void;
}

const UserSettingsPage: React.FC<UserSettingsPageProps> = ({ onClose, userCrew, onCrewUpdate }) => {
  const { user, updateUser, clearAuth } = useAuthStore();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [myCrew, setMyCrew] = useState<Crew | null>(null);
  const [leavingCrew, setLeavingCrew] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  
  // 프로필 이미지 관련 상태
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    birthday: '',
    church: '',
    position: ''
  });

  // 아바타 URL을 절대 경로로 변환하는 함수
  const getAvatarUrl = (avatarPath: string | undefined) => {
    if (!avatarPath) return null;
    if (avatarPath.startsWith('http')) return avatarPath;
    // API_URL에서 /api 제거 (정적 파일은 직접 서빙되므로)
    let API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
    API_URL = API_URL.replace(/\/api$/, ''); // /api 제거
    // 레거시 경로 호환 처리
    const normalized = avatarPath.replace(/^\/api\/uploads\//, '/uploads/');
    return `${API_URL}${normalized}`;
  };

  // 신급 옵션들
  const positionOptions = [
    '새신자',
    '성도',
    '집사',
    '권사',
    '안수집사',
    '장로',
    '목사',
    '전도사',
    '기타'
  ];

  useEffect(() => {
    loadUserProfile();
    // 전달받은 userCrew가 있으면 사용, 없으면 API에서 로드
    if (userCrew !== undefined) {
      setMyCrew(userCrew);
    } else {
      loadCrewInfo();
    }
  }, [userCrew]);

  const loadCrewInfo = async () => {
    try {
      const crewInfo = await crewAPI.getMyCrew();
      setMyCrew(crewInfo);
    } catch (error) {
      console.log('크루 정보를 로드할 수 없습니다:', error);
      // 크루 기능이 없어도 설정 페이지는 정상 작동
      setMyCrew(null);
    }
  };

  const handleLeaveCrew = async () => {
    if (!myCrew) {
      console.log('❌ 나갈 크루가 없음');
      return;
    }
    
    console.log('🚪 프로필 설정 크루 나가기 시작:', {
      crewName: myCrew.name,
      crewId: myCrew.id,
      memberCount: myCrew.memberCount
    });
    
    const confirmed = confirm(`정말로 "${myCrew.name}" 크루에서 나가시겠습니까?`);
    if (!confirmed) {
      console.log('❌ 사용자가 나가기를 취소함');
      return;
    }
    
    try {
      setLeavingCrew(true);
      console.log('🚪 프로필 설정에서 크루 나가기 API 호출 시작:', myCrew.name, myCrew.id);
      
      await crewAPI.leaveCrew(myCrew.id);
      console.log('✅ 크루 나가기 API 완료');
      
      // 로컬 상태 업데이트
      console.log('🔄 프로필 설정 로컬 상태 업데이트...');
      setMyCrew(null);
      onCrewUpdate?.(null); // 부모 컴포넌트에 알림
      
      // 크루 정보 새로고침
      console.log('🔄 크루 정보 새로고침 시작...');
      try {
        await loadCrewInfo();
        console.log('✅ 크루 정보 새로고침 완료');
      } catch (loadError) {
        console.error('❌ 크루 정보 새로고침 실패:', loadError);
      }
      
      console.log('✅ 프로필 설정 크루 나가기 완료');
      alert('크루에서 나갔습니다.');
    } catch (error: any) {
      console.error('❌ 프로필 설정 크루 나가기 실패:', {
        error: error,
        message: error.message,
        stack: error.stack,
        crewId: myCrew.id,
        crewName: myCrew.name
      });
      
      let errorMessage = '크루 나가기에 실패했습니다.';
      if (error.message) {
        console.log('📝 오류 메시지 파싱:', error.message);
        errorMessage = error.message;
      }
      
      // 특정 오류 타입별 처리
      if (error.message && error.message.includes('크루장은 나갈 수 없습니다')) {
        console.log('🚫 크루장 나가기 시도 오류');
        
        // 크루장 나가기 안내 메시지
        alert(
          '🚨 크루장은 일반 나가기가 불가능합니다.\n\n' +
          '📋 나가기 방법:\n' +
          '1️⃣ 크루 탭으로 이동\n' +
          '2️⃣ 크루를 선택하여 상세 페이지 열기\n' +
          '3️⃣ 권한 이관 버튼 클릭 (👥 아이콘)\n' +
          '4️⃣ 새로운 크루장 선택\n' +
          '5️⃣ "이관 후 나가기" 확인\n\n' +
          '✅ 권한 이관 후 자동으로 나가집니다!\n\n' +
          '또는 크루 삭제 옵션도 사용 가능합니다.'
        );
        
        return;
      } else if (error.message && error.message.includes('해당 크루의 멤버가 아닙니다')) {
        console.log('🔄 멤버가 아님 - 상태 동기화 시작');
        errorMessage = '이미 나갔거나 크루의 멤버가 아닙니다.';
        // 상태 동기화
        setMyCrew(null);
        onCrewUpdate?.(null);
        try {
          await loadCrewInfo();
          console.log('✅ 상태 동기화 완료');
        } catch (syncError) {
          console.error('❌ 상태 동기화 실패:', syncError);
        }
      } else if (error.message && error.message.includes('Network Error')) {
        console.log('🌐 네트워크 오류');
        errorMessage = '네트워크 연결을 확인하고 다시 시도해주세요.';
      } else if (error.message && error.message.includes('500')) {
        console.log('🖥️ 서버 오류');
        errorMessage = '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
      }
      
      console.log('🚨 최종 오류 메시지:', errorMessage);
      alert(`❌ ${errorMessage}`);
    } finally {
      setLeavingCrew(false);
    }
  };

  const loadUserProfile = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/users/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (response.ok) {
        const userData = await response.json();
        setProfile(userData);
        setFormData({
          name: userData.name || '',
          phone: userData.phone || '',
          birthday: userData.birthday ? userData.birthday.split('T')[0] : '',
          church: userData.church || '',
          position: userData.position || ''
        });
      }
    } catch (error) {
      console.error('프로필 로딩 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    // 유효성 검사
    if (formData.name.trim().length < 2) {
      alert('이름은 2자 이상 입력해주세요.');
      return;
    }

    if (formData.name.trim().length > 50) {
      alert('이름은 50자 이하로 입력해주세요.');
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/users/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          name: formData.name.trim(), // 공백 제거
          birthday: formData.birthday.trim() || null // 빈 문자열을 null로 변환
        })
      });

      if (response.ok) {
        const updatedUser = await response.json();
        updateUser(updatedUser);
        console.log('✅ 프로필 업데이트 성공');
        onClose();
      } else {
        const errorData = await response.json();
        console.error('프로필 업데이트 실패:', errorData);
        
        if (errorData.message === 'Validation error' && errorData.details) {
          alert(`입력 오류: ${errorData.details.join(', ')}`);
        } else {
          alert('프로필 업데이트에 실패했습니다. 다시 시도해주세요.');
        }
      }
    } catch (error) {
      console.error('프로필 업데이트 실패:', error);
      alert('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // 이미지 파일 선택 처리
  const handleAvatarSelect = () => {
    fileInputRef.current?.click();
  };

  // 파일 유효성 검사
  const validateImageFile = (file: File): string | null => {
    // 파일 크기 검사 (5MB 제한)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return '이미지 크기는 5MB 이하여야 합니다.';
    }

    // 파일 형식 검사
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return 'JPG, PNG, WEBP 형식의 이미지만 업로드 가능합니다.';
    }

    return null;
  };

  // 파일 선택 시 처리
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 파일 유효성 검사
    const validationError = validateImageFile(file);
    if (validationError) {
      alert(validationError);
      return;
    }

    // 미리보기 생성
    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // 서버에 업로드
    await uploadAvatar(file);
  };

  // 아바타 이미지 업로드
  const uploadAvatar = async (file: File) => {
    setIsUploadingAvatar(true);
    
    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/users/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        
        // 프로필 정보 업데이트
        setProfile(prev => prev ? { ...prev, avatar: result.avatarUrl } : null);
        
        // Auth Store 업데이트
        if (user) {
          updateUser({ ...user, avatar: result.avatarUrl });
        }
        
        console.log('✅ 프로필 이미지 업로드 성공:', result.avatarUrl);
        
        // 성공 메시지를 더 사용자 친화적으로 표시
        const successMessage = '프로필 이미지가 성공적으로 변경되었습니다!';
        alert(successMessage);
      } else {
        const errorData = await response.json();
        
        // 서버 에러 메시지에 따른 구체적인 안내
        let errorMessage = errorData.message || '이미지 업로드에 실패했습니다.';
        
        if (response.status === 413) {
          errorMessage = '파일 크기가 너무 큽니다. 5MB 이하의 이미지를 선택해주세요.';
        } else if (response.status === 415) {
          errorMessage = '지원하지 않는 파일 형식입니다. JPG, PNG, WEBP 파일을 선택해주세요.';
        } else if (response.status === 401) {
          errorMessage = '로그인이 필요합니다. 다시 로그인해주세요.';
        } else if (response.status >= 500) {
          errorMessage = '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
        }
        
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('프로필 이미지 업로드 실패:', error);
      
      // 네트워크 에러 처리
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        alert('네트워크 연결을 확인해주세요. 서버가 응답하지 않습니다.');
      } else {
        alert(error.message || '이미지 업로드 중 오류가 발생했습니다.');
      }
      
      setAvatarPreview(null); // 미리보기 제거
    } finally {
      setIsUploadingAvatar(false);
      // 파일 입력 초기화
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // 회원 탈퇴 처리
  const handleDeleteAccount = async () => {
    // 첫 번째 확인
    const firstConfirm = confirm(
      '⚠️ 회원 탈퇴를 진행하시겠습니까?\n\n' +
      '탈퇴 시 다음 정보가 삭제됩니다:\n' +
      '• 프로필 정보\n' +
      '• 크루 멤버십\n' +
      '• 작성한 게시물 및 댓글\n' +
      '• 기도/묵상 기록\n\n' +
      '이 작업은 되돌릴 수 없습니다.'
    );
    
    if (!firstConfirm) return;
    
    // 두 번째 확인 (더 강한 경고)
    const secondConfirm = confirm(
      '🚨 정말로 탈퇴하시겠습니까?\n\n' +
      '모든 데이터가 영구적으로 삭제되며\n' +
      '복구가 불가능합니다.\n\n' +
      '정말 탈퇴하시려면 "확인"을 눌러주세요.'
    );
    
    if (!secondConfirm) return;
    
    try {
      setIsDeletingAccount(true);
      
      const response = await usersAPI.deleteAccount();
      
      if (response.status === 200 || response.status === 204) {
        alert('회원 탈퇴가 완료되었습니다.\n이용해주셔서 감사합니다.');
        
        // 로그아웃 처리
        clearAuth();
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('loginExpiry');
        
        // 로그인 페이지로 리다이렉트
        window.location.href = '/';
      }
    } catch (error: any) {
      console.error('회원 탈퇴 실패:', error);
      
      let errorMessage = '회원 탈퇴 중 오류가 발생했습니다.';
      
      if (error.response?.status === 400) {
        // 크루장인 경우
        const errorData = error.response.data;
        if (errorData?.message?.includes('크루장')) {
          errorMessage = errorData.message + '\n\n먼저 크루장 권한을 다른 멤버에게 이관하거나 크루를 삭제해주세요.';
        } else {
          errorMessage = errorData?.message || errorMessage;
        }
      } else if (error.response?.status === 401) {
        errorMessage = '로그인이 만료되었습니다. 다시 로그인해주세요.';
      } else if (error.response?.status >= 500) {
        errorMessage = '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
      }
      
      alert(`❌ ${errorMessage}`);
    } finally {
      setIsDeletingAccount(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return createPortal(
    <div className="fixed inset-0 bg-white z-[9999] overflow-y-auto">
      {/* 헤더 */}
      <div className="bg-gradient-to-r from-slate-600 to-slate-800 text-white p-4 sticky top-0 z-10">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-lg font-bold">프로필 설정</h1>
          </div>
          <button
            onClick={handleSave}
            disabled={isSaving || formData.name.trim().length < 2 || formData.name.trim().length > 50}
            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl transition-colors disabled:opacity-50"
          >
            <Save size={16} />
            {isSaving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>

      {/* 내용 */}
      <div className="max-w-md mx-auto p-4 pb-20 space-y-6">
        {/* 프로필 사진 */}
        <div className="text-center">
          <div className="relative inline-block">
            <div 
              className="cursor-pointer hover:opacity-80 transition-opacity relative overflow-hidden mx-auto mb-3"
              onClick={handleAvatarSelect}
            >
              <Avatar 
                src={avatarPreview || profile?.avatar}
                alt="프로필"
                size="xl"
                fallbackIcon={<User size={32} className="text-white" />}
              />
              
              {/* 업로드 중 오버레이 */}
              {isUploadingAvatar && (
                <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                </div>
              )}
              
              {/* 카메라 아이콘 오버레이 */}
              <div className="absolute inset-0 bg-black/30 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                <Camera size={20} className="text-white" />
              </div>
            </div>
            
            {/* 업로드 버튼 */}
            <button
              onClick={handleAvatarSelect}
              disabled={isUploadingAvatar}
              className="absolute -bottom-1 -right-1 w-8 h-8 bg-rose-500 hover:bg-rose-600 text-white rounded-full flex items-center justify-center transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              title="프로필 사진 변경"
            >
              {isUploadingAvatar ? (
                <div className="animate-spin rounded-full h-4 w-4 border border-white border-t-transparent"></div>
              ) : (
                <Upload size={14} />
              )}
            </button>
            
            {/* 숨겨진 파일 입력 */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
          
          <p className="text-sm text-slate-500 mb-2">{profile?.email}</p>
          <p className="text-xs text-slate-400">프로필 사진을 클릭하여 변경하세요</p>
        </div>

        {/* 폼 필드들 */}
        <div className="space-y-4">
          {/* 이름 */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
              <User size={16} />
              이름
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent transition-colors ${
                formData.name.trim().length < 2 && formData.name.length > 0
                  ? 'border-red-300 focus:ring-red-500 bg-red-50'
                  : formData.name.trim().length >= 2
                  ? 'border-green-300 focus:ring-green-500 bg-green-50'
                  : 'border-slate-300 focus:ring-slate-500'
              }`}
              placeholder="이름을 입력하세요"
              maxLength={50}
            />
            {/* 유효성 검사 메시지 */}
            <div className="mt-1 min-h-[20px]">
              {formData.name.length > 0 && formData.name.trim().length < 2 && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  ⚠️ 이름은 2자 이상 입력해주세요
                </p>
              )}
              {formData.name.trim().length >= 2 && formData.name.trim().length <= 50 && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  ✅ 사용 가능한 이름입니다
                </p>
              )}
              {formData.name.length > 50 && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  ⚠️ 이름은 50자 이하로 입력해주세요
                </p>
              )}
              <p className="text-xs text-slate-400 mt-1">
                {formData.name.length}/50자
              </p>
            </div>
          </div>

          {/* 전화번호 */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
              <Phone size={16} />
              전화번호
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent"
              placeholder="010-0000-0000"
            />
          </div>

          {/* 생일 */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
              <Calendar size={16} />
              생일
            </label>
            <input
              type="date"
              value={formData.birthday}
              onChange={(e) => handleInputChange('birthday', e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent"
            />
          </div>

          {/* 출석교회 */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
              <Church size={16} />
              출석교회
            </label>
            <input
              type="text"
              value={formData.church}
              onChange={(e) => handleInputChange('church', e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent"
              placeholder="교회명을 입력하세요"
            />
          </div>

          {/* 신급 */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
              <Award size={16} />
              신급
            </label>
            <select
              value={formData.position}
              onChange={(e) => handleInputChange('position', e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent"
            >
              <option value="">신급을 선택하세요</option>
              {positionOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {/* 크루 정보 */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
              <Users size={16} />
              소속 크루
            </label>
            {myCrew ? (
              <div className="space-y-4">
                {/* 크루 기본 정보 */}
                <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {myCrew.image ? (
                        <img 
                          src={myCrew.image}
                          alt={myCrew.name}
                          className="w-12 h-12 rounded-xl object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-slate-600 to-slate-800 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                          {myCrew.name[0]}
                        </div>
                      )}
                      <div>
                        <h4 className="font-semibold text-slate-800 text-lg">{myCrew.name}</h4>
                        <p className="text-sm text-slate-600">
                          리더: {myCrew.leader?.name || '알 수 없음'}
                        </p>
                        {myCrew.description && (
                          <p className="text-xs text-slate-500 mt-1">{myCrew.description}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={handleLeaveCrew}
                      disabled={leavingCrew}
                      className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <LogOut size={14} className="inline mr-1" />
                      {leavingCrew ? '나가는 중...' : '나가기'}
                    </button>
                  </div>
                  
                  {/* 크루 통계 */}
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t border-slate-200">
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-700">{myCrew.memberCount || 0}</p>
                      <p className="text-xs text-slate-500">총 멤버</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-700">{myCrew.maxMembers || 50}</p>
                      <p className="text-xs text-slate-500">최대 인원</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-700">
                        {myCrew.createdAt ? new Date(myCrew.createdAt).toLocaleDateString('ko-KR', { 
                          month: 'short', 
                          day: 'numeric' 
                        }) : '-'}
                      </p>
                      <p className="text-xs text-slate-500">생성일</p>
                    </div>
                  </div>
                </div>

                {/* 크루 멤버 목록 */}
                {myCrew.members && myCrew.members.length > 0 && (
                  <div className="p-4 bg-white rounded-xl border border-slate-200">
                    <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                      <Users size={16} className="text-slate-600" />
                      크루 멤버 ({myCrew.members.length}명)
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {myCrew.members.map((member, index) => (
                        <div key={member.id || index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                          <div className="w-10 h-10 bg-gradient-to-br from-slate-400 to-slate-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {member.name?.charAt(0) || '?'}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-800">{member.name || '알 수 없음'}</span>
                              {member.role === 'leader' && (
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full font-medium">
                                  👑 리더
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500">
                              {member.church || '소속 교회 정보 없음'}
                            </p>
                          </div>
                          <div className="text-right">
                            {member.hasSharedToday ? (
                              <div className="flex flex-col items-end gap-1">
                                <div className="flex items-center gap-1 text-green-600">
                                  <CheckCircle size={14} />
                                  <span className="text-xs font-medium">완료</span>
                                </div>
                                {member.completedAt && (
                                  <span className="text-xs text-green-500 font-medium">
                                    {new Date(member.completedAt).toLocaleTimeString('ko-KR', { 
                                      hour: '2-digit', 
                                      minute: '2-digit',
                                      hour12: false 
                                    })}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-slate-400">
                                <Clock size={14} />
                                <span className="text-xs">대기중</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-center">
                <p className="text-slate-500 text-sm">가입한 크루가 없습니다.</p>
                <p className="text-xs text-slate-400 mt-1">묵상을 완료하면 크루에 가입할 수 있습니다.</p>
              </div>
            )}
          </div>

          {/* 회원 탈퇴 섹션 */}
          <div className="mt-8 pt-8 border-t border-red-200">
            <div className="p-4 bg-red-50 rounded-xl border border-red-200">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-800 mb-1">회원 탈퇴</h3>
                  <p className="text-sm text-red-700">
                    탈퇴 시 모든 데이터가 삭제되며 복구가 불가능합니다.
                    {myCrew && (
                      <span className="block mt-1 font-medium">
                        ⚠️ 크루장으로 활동 중인 크루가 있으면 먼저 권한을 이관해주세요.
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeletingAccount ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>탈퇴 처리 중...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    <span>회원 탈퇴</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UserSettingsPage;
